import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    BlobType,
    FileAction,
    FileColumnKey,
    FileModel,
    FolderViewModel,
    ItemsToBeRemovedOptions,
    ListSearch,
    MEDFileAction,
    MEDFileColumnKeys,
    OptionItem,
    SortOption,
    UploadFileItem,
} from '@ca/core/models';
import { DialogService, ExperimentalFeaturesService, FileService, FileUploadService, SignalrService, UserNotificationService } from '@ca/core/services';
import { environment } from '@ca/environment/environment';
import { mapFolderPathInfoToFolderViewModel, mapFolderToFolderViewModel } from '@ca/utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import deepEqual from 'deep-equal';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { EMPTY, from, of, Subject } from 'rxjs';
import { catchError, skip, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FileColumn } from 'src/app/core/models/file-column.model';
import { Folder } from 'src/app/core/models/folder.model';
import { RdpFilesSubmissionDoneEvent } from 'src/app/core/models/rdp-files-submission-done-event';
import { FileExplorerMode } from 'src/app/core/types/file-explorer-mode.type';
import { FileExplorerHistoryService } from 'src/app/shared/services/file-explorer-history.service';
import { defaultPageNumber, defaultPageSize, FILE_EXPLORER_MODE, UPLOADER_KEY } from '../../constants/constants';
import { CheckExistenceOfItemDialogComponent } from '../check-existence-of-item-dialog/check-existence-of-item-dialog.component';
import { MEDUploadDataDialogComponent } from '../upload-data-MED/MED-upload-data-dialog.component';
import { MyFilesFacadeService } from './services/my-files-facade.service';

@UntilDestroy()
@Component({
    selector: 'ca-my-files',
    templateUrl: './my-files.component.html',
    styleUrls: ['./my-files.component.scss'],
    providers: [MyFilesFacadeService, FileExplorerHistoryService, SignalrService],
})
export class MyFilesComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() mode: FileExplorerMode = FILE_EXPLORER_MODE.MODIFICATION;
    @Input() updateParameterQuery: boolean = true;
    @Input() truncateFileNameAtWidthOf?: number;
    @Input() titleKey: string;
    @Input() isUploadOfFilesEnabled?: boolean;
    @Input() isFilesPage: boolean = true;
    @Input() isFilesStatusShown?: boolean;
    @Input() isUploadModal?: boolean;

    @Input() actions?: FileAction[] | MEDFileAction[] = ['view', 'rename', 'share', 'download', 'delete'];
    @Input() columns?: FileColumn<FileColumnKey>[] | FileColumn<MEDFileColumnKeys>[] = [
        { key: 'checkbox' },
        {
            key: 'createdAt',
            translationKey: 'GENERAL.COLUMN.CREATION_DATE',
            defaultValue: '\u2014',
            transform: 'resultDate',
            transformDateFormat: 'dd.MM.yyyy',
            transformTimeFormat: 'HH:mm:ss',
        },
        {
            key: 'sizeInBytes',
            translationKey: 'GENERAL.COLUMN.SIZE',
            defaultValue: '\u2014',
            transform: 'fileSize',
        },
        {
            key: 'type',
            translationKey: 'GENERAL.COLUMN.TYPE',
            defaultValue: '\u2014',
            transform: 'lowercase',
        },
    ];

    @Output() onClickOnFile = new EventEmitter<FileModel>();
    @Output() onSelectMultipleFiles = new EventEmitter<FileModel[]>();

    get totalFileResultsText(): string {
        const totalNumOfFiles = this.totalNumberOfFiles;
        return totalNumOfFiles === 1
            ? this.translateService.instant('MY_WORKSPACE.MY_FILES.RESULT_FILE')
            : this.translateService.instant('MY_WORKSPACE.MY_FILES.RESULTS_FILES', { totalNumOfFiles });
    }

    sortOptions: OptionItem<SortOption>[] = [
        { label: 'Sort by date desc', value: '-createdAt' },
        { label: 'Sort by date asc', value: 'createdAt' },
        { label: 'Sort by A-Z', value: 'title' },
        { label: 'Sort by Z-A', value: '-title' },
        { label: 'Sort by size', value: 'size' },
        { label: 'Sort by type', value: 'type' },
    ];
    userFileBlobType = BlobType.UserFile;
    defaultPageSize = defaultPageSize;
    currentSearchParams: ListSearch = {
        categoryIds: '',
        pageNumber: defaultPageNumber,
        pageSize: this.defaultPageSize,
        searchQuery: '',
        sortBy: <SortOption>this.sortOptions[0].value,
    };

    currentFolder$ = this.myFilesFacadeService.currentFolder$;
    filesAndFolders: (FileModel | Folder)[] = [];
    totalNumberOfFiles: number;
    totalItemsCount$ = this.myFilesFacadeService.totalItemsCount$;
    isFolderLoading$ = this.myFilesFacadeService.isFolderLoading$;
    FILE_EXPLORER_MODE = FILE_EXPLORER_MODE;
    UPLOADER_KEY = UPLOADER_KEY;
    clearSearchQuery: number;
    uploader: FileUploader | null;
    showUploadDetails = false;
    filesStorageKey: string = 'filesPageSize';
    isMEDprojectEnabled = this.experimentalFeaturesService.isMEDProjectActive;
    cancelUpload: FileItem;
    cancelAllUploads: { cancel: boolean };
    initialUploaderQueueLength: number;
    fileExplorerHistory: FolderViewModel[];
    isSearchLoading: boolean;
    isPageSizeModifiable: boolean = true;
    initialValueOfFilesToBeUploaded$ = this.fileUploadService.initialValueOfFilesToBeUploaded$;
    private newFolder$ = this.myFilesFacadeService.newFolder$;
    private deleteFoldersAndFiles$ = this.myFilesFacadeService.deleteFoldersAndFiles$;
    private selectAllFilteredFilesOfFolder$ = this.myFilesFacadeService.selectAllFilteredFilesOfFolder$;
    private error$ = this.myFilesFacadeService.error$;
    private currentFolderId: string | undefined;
    private currentFolderQueryParam: Params;
    private uploadMEDDataDialogClosed$$ = new Subject<boolean>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private dialogService: DialogService,
        private fileExplorerHistoryService: FileExplorerHistoryService,
        private fileUploadService: FileUploadService,
        private modalService: NgbModal,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private myFilesFacadeService: MyFilesFacadeService,
        private router: Router,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private signalRService: SignalrService,
        private fileService: FileService,
    ) {}

    ngOnInit(): void {
        this.activatedRoute.queryParamMap
            .pipe(untilDestroyed(this))
            .pipe(
                tap(queryParamMap => {
                    this.currentFolderId = queryParamMap.get('folderId') || undefined;
                }),
            )
            .subscribe();
        this.isFolderLoading$
            .pipe(untilDestroyed(this))
            .pipe(
                tap(loading => {
                    if (loading) {
                        this.userNotificationService.showSpinner();
                        this.isSearchLoading = true;
                    } else {
                        this.userNotificationService.hideSpinner();
                        this.isSearchLoading = false;
                    }
                }),
            )
            .subscribe();
        this.currentFolder$
            .pipe(skip(1), untilDestroyed(this))
            .pipe(
                tap((folder: Folder | undefined) => {
                    if (folder) {
                        if (this.mode === FILE_EXPLORER_MODE.MODIFICATION) {
                            this.currentFolderQueryParam = {
                                folderId: folder.id,
                                // if we go from one to an other folder we have to reset the searchQuery and page
                                q: this.currentSearchParams.searchQuery,
                                page: this.currentSearchParams.pageNumber,
                            };
                            if (this.currentFolderId) {
                                this.router.navigate([], {
                                    relativeTo: this.activatedRoute,
                                    queryParams: this.currentFolderQueryParam,
                                    queryParamsHandling: 'merge',
                                });
                            }
                        }
                        this.filesAndFolders = [...folder.folders, ...folder.files];
                        this.totalNumberOfFiles = folder.totalFilesCount;

                        if (this.fileExplorerHistoryService.getHistory().length === 0 && folder.folderPath.length > 1) {
                            folder.folderPath.forEach(fp => this.fileExplorerHistoryService.navigateTo(mapFolderPathInfoToFolderViewModel(fp)));
                        } else {
                            this.fileExplorerHistoryService.navigateTo(mapFolderToFolderViewModel(folder));
                        }
                        this.currentFolderId = folder.id;
                    }
                }),
            )
            .subscribe();
        this.newFolder$
            .pipe(skip(1), untilDestroyed(this))
            .pipe(
                tap(() => {
                    this.setCurrentSearchParamsAndOpenFolder(this.currentSearchParams);
                }),
            )
            .subscribe();
        this.deleteFoldersAndFiles$
            .pipe(
                skip(1),
                tap(() => {
                    this.setCurrentSearchParamsAndOpenFolder(this.currentSearchParams);
                }),
                untilDestroyed(this),
            )
            .subscribe();
        this.selectAllFilteredFilesOfFolder$
            .pipe(
                skip(1),
                tap((files: FileModel[]) => this.onSelectMultipleFiles.emit(files)),
                untilDestroyed(this),
            )
            .subscribe();
        this.error$
            .pipe(
                skip(1),
                tap((err: string | { statusOrMessage: string; folderName: string } | undefined) => {
                    if (err) {
                        if (typeof err === 'string') {
                            this.userNotificationService.showError(err);
                        } else {
                            // we receive 409 only if we try to create a folder with the name that already exists
                            if (err.statusOrMessage === '409') {
                                this.userNotificationService.showError(
                                    this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_WITH_THE_SAME_NAME_ALREADY_EXISTS', {
                                        folderName: err.folderName,
                                    }) as string,
                                );
                            } else {
                                if (err.folderName) {
                                    this.userNotificationService.showError(this.translateService.instant(err.statusOrMessage, err.folderName) as string);
                                } else {
                                    this.userNotificationService.showError(err.statusOrMessage);
                                }
                            }
                        }
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.connectToSignalR();
    }

    ngOnDestroy(): void {
        this.signalRService.stop();
    }

    ngAfterViewInit(): void {
        // setTimeout to avoid expression has changed after it was checked exception
        setTimeout(() => {
            this.uploader = this.fileUploadService.fetchFromCache(UPLOADER_KEY.MY_FILES_UPLOAD_UPLOADER_KEY);
            // fetch the initial files length from service to correctly calculate upload progress in upload progress container
            this.fileUploadService.loadInitialValueOfFiles();
            this.initialUploaderQueueLength = this.uploader?.queue?.length || 0;
        });
    }

    onPageSizeChange(newSize: number): void {
        if (this.defaultPageSize !== newSize) {
            this.defaultPageSize = newSize;
            this.currentSearchParams = {
                categoryIds: '',
                pageNumber: defaultPageNumber,
                pageSize: this.defaultPageSize,
                searchQuery: '',
                sortBy: <SortOption>this.sortOptions[0].value,
            };
            this.setCurrentSearchParamsAndOpenFolder(this.currentSearchParams);
        }
    }

    onTriggerSearch(searchParams: ListSearch): void {
        if (deepEqual(this.currentSearchParams, searchParams) && deepEqual({ folderId: this.currentFolderId } as Params, this.currentFolderQueryParam)) {
            return;
        }
        this.setCurrentSearchParamsAndOpenFolder(searchParams);
    }

    deleteBySearchParams(): void {
        const totalNumOfFiles = this.totalNumberOfFiles;
        const deleteMessage =
            totalNumOfFiles === 1
                ? this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_BY_SEARCH_PARAMS_DIALOG_MESSAGE_ONE_FILE')
                : this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_BY_SEARCH_PARAMS_DIALOG_MESSAGE_MORE_THEN_ONE_FILE', { totalNumOfFiles });
        const confirmButtonText = this.translateService.instant('GENERAL.DELETE');
        const modalTitle = this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_ALL_FILES');
        const confirmModal = this.dialogService.openConfirm({
            message: deleteMessage,
            confirmText: confirmButtonText,
            modalTitle: modalTitle,
            customCssClassConfirmBtn: 'btn-primary-red',
        });

        confirmModal.result
            .then(() => {
                this.myFilesFacadeService.deleteBySearchParams(this.currentSearchParams, this.currentFolderId);
            })
            .catch(() => {
                // do nothing
            });
    }

    onHandleBreadcrumbs(history: FolderViewModel[]): void {
        this.fileExplorerHistory = history;
    }

    onUploadCompleted(): void {
        this.setCurrentSearchParamsAndOpenFolder(this.currentSearchParams);
        this.fileUploadService.resetInitialValueOfFiles();
    }

    onChangeCustomTags(event: { customTags: string[]; fileIDs: string[] }): void {
        const { customTags, fileIDs } = event;

        this.filesAndFolders = this.filesAndFolders.map(file => {
            if (fileIDs.includes(file.id) && file instanceof FileModel) {
                const updatedFile = FileModel.createInstance({
                    ...file,
                    metadata: {
                        ...file.metadata,
                        customTags,
                    },
                    name: file.name,
                    token: file.token,
                });
                this.fileService.updateTagsMetadata$(file.id, { tags: customTags }).subscribe();
                return updatedFile;
            }
            return file; // Return unchanged file for Folder<FileModel> or unmatched IDs
        }) as Array<FileModel | Folder<FileModel>>;
    }

    delete(deleteOptions: ItemsToBeRemovedOptions<FileModel | Folder>): void {
        let modalTitle: string;
        let message: string;
        const confirmText = this.translateService.instant('GENERAL.DELETE');
        if (deleteOptions.items.every(f => f instanceof FileModel || f instanceof FileModel)) {
            if (deleteOptions.items.length > 1) {
                modalTitle = this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_MODAL.DELETE_FILES_TITLE');
                message = this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_MODAL.DELETE_FILES_MESSAGE');
            } else {
                modalTitle = this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_MODAL.DELETE_FILE_TITLE');
                message = this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_MODAL.DELETE_FILE_MESSAGE', {
                    filename: deleteOptions.items[0].name,
                });
            }
        } else {
            modalTitle = this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_MODAL.DELETE_FOLDERS_TITLE');
            message = this.translateService.instant('MY_WORKSPACE.MY_FILES.DELETE_MODAL.DELETE_FOLDERS_MESSAGE');
        }

        const confirmModal = this.dialogService.openConfirm({ modalTitle, message, confirmText });

        confirmModal.result
            .then(() => {
                this.myFilesFacadeService.deleteFoldersAndFiles(deleteOptions.items);
            })
            .catch(() => null);
    }

    createFolder(): void {
        const modalRef = this.modalService.open(CheckExistenceOfItemDialogComponent, { size: 'md' });
        const componentInstance = modalRef.componentInstance as CheckExistenceOfItemDialogComponent;

        componentInstance.title = this.translateService.instant('MY_WORKSPACE.MY_FILES.NAME_NEW_FOLDER');
        componentInstance.inputLabel = this.translateService.instant('MY_WORKSPACE.MY_FILES.NEW_FOLDER_NAME_INPUT_PLACEHOLDER');
        componentInstance.confirmText = this.translateService.instant('GENERAL.CREATE');
        componentInstance.checkExistenceType = 'folder';

        from(modalRef.result)
            .pipe(
                switchMap((folderName: string) => {
                    return of(this.myFilesFacadeService.createFolder(this.currentFolderId!, folderName));
                }),
                catchError((error: unknown) => {
                    if (error !== 'canceled') {
                        this.userNotificationService.showError('MY_WORKSPACE.MY_FILES.ERROR_CREATING_FOLDER');
                    }
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    uploadMEDdata(): void {
        let selectedFiles: FileModel[] = [];
        const modal: NgbModalRef = this.modalService.open(MEDUploadDataDialogComponent, {
            size: 'xl',
            centered: true,
        });
        const uploadMEDDataDialogComponent = modal.componentInstance;
        uploadMEDDataDialogComponent.modalTitleTranslationKey = 'UPLOAD_MED_FILES_DIALOG.TITLE';
        uploadMEDDataDialogComponent.nameLabelTranslationKey = 'APPLICATIONS.RUN_APPLICATION.NAME_LABEL';
        uploadMEDDataDialogComponent.namePlaceholderTranslationKey = 'APPLICATIONS.RUN_APPLICATION.NAME_PLACEHOLDER';
        uploadMEDDataDialogComponent.uploadButtonTranslationKey = 'UPLOAD_MED_FILES_DIALOG.UPLOAD_DATA';
        uploadMEDDataDialogComponent.parentFolderName = 'Data';
        uploadMEDDataDialogComponent.isButtonDisabled = true;
        uploadMEDDataDialogComponent.operationId = 'operationId';
        uploadMEDDataDialogComponent.currentFolderId = this.currentFolderId;
        uploadMEDDataDialogComponent.breadcrumbs = this.fileExplorerHistoryService.getHistory();
        uploadMEDDataDialogComponent.selectedFiles = [...selectedFiles];

        uploadMEDDataDialogComponent.closed.subscribe(() => {
            this.uploadMEDDataDialogClosed$$.next(true);
        });

        uploadMEDDataDialogComponent.dismissed.subscribe(() => {
            this.uploadMEDDataDialogClosed$$.next(true);
        });

        uploadMEDDataDialogComponent.selectedFilesUpdatedBecauseOfUpload
            .pipe(
                tap((files: FileModel[]) => {
                    selectedFiles = files;
                }),
            )
            .subscribe();

        uploadMEDDataDialogComponent.removeFiles
            .pipe(
                tap((removeFilesOptions: ItemsToBeRemovedOptions<FileModel>) => {
                    const remainingFiles = selectedFiles.filter((file: FileModel) => !removeFilesOptions.items.some(f => file.id === f.id));
                    selectedFiles = remainingFiles;
                    uploadMEDDataDialogComponent.selectedFiles = [...selectedFiles];
                    uploadMEDDataDialogComponent.isButtonDisabled = uploadMEDDataDialogComponent.selectedFiles.length === 0;
                }),
                takeUntil(this.uploadMEDDataDialogClosed$$),
            )
            .subscribe();

        uploadMEDDataDialogComponent.addFiles
            .pipe(
                tap((filesToAdd: FileModel[]) => {
                    selectedFiles = [...filesToAdd, ...selectedFiles];
                    uploadMEDDataDialogComponent.isButtonDisabled = selectedFiles.length === 0;
                }),
                takeUntil(this.uploadMEDDataDialogClosed$$),
            )
            .subscribe();
    }

    openFolder(folderId?: string): void {
        if (folderId && folderId !== this.currentFolderId) {
            this.clearSearchQuery = new Date().getTime();
            this.currentSearchParams.searchQuery = '';
            this.currentSearchParams.pageNumber = 1;
        }
        this.myFilesFacadeService.getFolder(folderId, this.currentSearchParams);
    }

    handleClickOnFolder(id: string): void {
        if (this.mode === FILE_EXPLORER_MODE.SELECTION) {
            this.openFolder(id);
        }
    }

    handleClickOnFile(file: FileModel): void {
        if (this.mode === FILE_EXPLORER_MODE.SELECTION) {
            this.onClickOnFile.emit(file);
        }
    }

    filesAdded(files: UploadFileItem[]): void {
        this.initialUploaderQueueLength = this.uploader?.queue?.length || 0;
        // store data to calculate the correct percentage of uploaded files (base must be initial number of files).
        // Calculation is needed to take into account files that user might additionally upload while other files in the uploading process.
        if (this.fileUploadService.isAnyUploadInProgress()) {
            this.fileUploadService.setInitialValueOfFiles(this.fileUploadService.loadInitialValueOfFiles() + files.length);
        } else {
            this.fileUploadService.setInitialValueOfFiles(this.initialUploaderQueueLength);
        }
    }

    cancelFileUpload(file: FileItem): void {
        this.fileUploadService.setInitialValueOfFiles(this.fileUploadService.loadInitialValueOfFiles() - 1);
        this.cancelUpload = file;
    }

    cancelAllFileUploads(): void {
        this.cancelAllUploads = { cancel: true };
    }

    uploadCancelled(): void {
        this.initialUploaderQueueLength = this.uploader?.queue?.length || 0;
    }

    selectAllFilteredFiles(): void {
        this.myFilesFacadeService.selectAllFilteredFilesOfFolder(this.currentFolderId!, this.currentSearchParams);
    }

    renameFolder(folder: Folder): void {
        const modalRef = this.modalService.open(CheckExistenceOfItemDialogComponent, { size: 'md' });
        const componentInstance = modalRef.componentInstance as CheckExistenceOfItemDialogComponent;

        componentInstance.title = this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_RENAME');
        componentInstance.inputLabel = this.translateService.instant('GENERAL.NEW_NAME');
        componentInstance.confirmText = this.translateService.instant('GENERAL.RENAME');
        componentInstance.oldName = folder.name;
        componentInstance.checkExistenceType = 'folder';

        from(modalRef.result)
            .pipe(
                switchMap((name: string) => {
                    return of(this.myFilesFacadeService.renameFolder(folder, name));
                }),
                catchError((error: unknown) => {
                    if (error !== 'canceled') {
                        this.userNotificationService.showError('MY_WORKSPACE.MY_FILES.ERROR_FOLDER_RENAME');
                    }
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    getTitle(): string {
        return this.translateService.instant(this.titleKey || 'FILE_EXPLORER.TABS.MY_FILES');
    }

    private connectToSignalR(): void {
        const callback = (rdpFilesSubmissionDoneEvent: RdpFilesSubmissionDoneEvent | undefined): void => {
            if (!rdpFilesSubmissionDoneEvent) {
                console.error('Received an unexpected value from /rdpnotifications, RdpFilesSubmissionDoneEvent', rdpFilesSubmissionDoneEvent);
                return;
            }
            if (rdpFilesSubmissionDoneEvent.isError) {
                this.userNotificationService.showError(rdpFilesSubmissionDoneEvent.message, '', undefined, false);
            } else {
                this.userNotificationService.showSuccess(rdpFilesSubmissionDoneEvent.message, '', false);
            }

            console.log('mmmm', rdpFilesSubmissionDoneEvent);

            // Folder ID matches the current one, trigger the folder update
            if (this.currentFolderId === rdpFilesSubmissionDoneEvent.folderId) {
                setTimeout(() => {
                    this.setCurrentSearchParamsAndOpenFolder(this.currentSearchParams);
                }, 500);
            }
        };

        this.signalRService.startConnection(environment.websocketOrigin + '/rdpnotifications', 'RdpFilesSubmissionDoneEvent', callback);
    }

    private setCurrentSearchParamsAndOpenFolder(searchParams: ListSearch): void {
        this.currentSearchParams = searchParams;
        this.openFolder(this.currentFolderId);
    }
}
